import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import { toast } from "react-toastify";
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  InputAdornment,
  MenuItem,
  Paper,
  Popover,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SearchIcon from "@mui/icons-material/Search";
import FlashOnIcon from "@mui/icons-material/FlashOn";
import StarIcon from "@mui/icons-material/Star";
import { useGlobalContext } from "../../context/global";
import { HistoryOutlined } from "@mui/icons-material";
import { getMappedCustomAttributeLabel } from "../../components/Filter/FilterData/columnConfig";
import ExportDownloadTooltip from "../../components/Reports/Components/ExportDownloadTooltip";
import CoreSizeLogsModal from "./CoreSizeLogsModal";

const PRIMARY = "#6148f6";
const OPTION_ROW_SELECTED_BG = "rgba(97, 72, 246, 0.06)";
const TILE_RECOMMENDED_BG = "rgba(97, 72, 246, 0.08)";
const CORE_SIZE_COLUMN_HINT =
  "Size totals are combined across styles; Core Size evaluation runs at the style ID level.";
const AVAILABILITY_STOCK_COLUMN_DESC =
  "If both Availability Stock & Availability Stock % are set, the lower calculated stock value is used.";

function isRuleValueSet(value) {
  return value == null || value === ""
    ? false
    : !Number.isNaN(Number(value));
}

/** Fallback percent: null/undefined → empty; 0 is a valid value. */
function toFallbackPercentInputValue(rawPct) {
  if (rawPct == null || rawPct === "") return "";
  const asPercent = Math.round(Number(rawPct) * 100);
  if (!Number.isFinite(asPercent) || asPercent < 0) return "";
  return sanitizePercentInput(asPercent);
}

/** Fallback absolute: null/undefined → empty; 0 is a valid value. */
function toFallbackAbsInputValue(rawAbs) {
  if (rawAbs == null || rawAbs === "") return "";
  const num = Number(rawAbs);
  if (!Number.isFinite(num) || num < 0) return "";
  return sanitizeNonNegativeIntegerInput(num);
}

/** Keep only 0–100 integers; block negatives and decimals. */
function sanitizePercentInput(raw) {
  if (raw == null || raw === "") return "";
  const digitsOnly = String(raw).replace(/\D/g, "");
  if (digitsOnly === "") return "";
  const num = Number(digitsOnly);
  if (!Number.isFinite(num)) return "";
  return String(Math.min(100, num));
}

function parsePercentInput(value) {
  if (!isRuleValueSet(value)) return null;
  const num = Math.trunc(Number(value));
  if (!Number.isFinite(num) || num < 0 || num > 100) return null;
  return num;
}

function blockNonIntegerPercentKeys(event) {
  if (["e", "E", "+", "-", ".", ","].includes(event.key)) {
    event.preventDefault();
  }
}

/** Non-negative integers only; block minus and decimals. */
function sanitizeNonNegativeIntegerInput(raw) {
  if (raw == null || raw === "") return "";
  const digitsOnly = String(raw).replace(/\D/g, "");
  if (digitsOnly === "") return "";
  const num = Number(digitsOnly);
  if (!Number.isFinite(num)) return "";
  return String(num);
}

function parseNonNegativeIntegerInput(value) {
  if (!isRuleValueSet(value)) return null;
  const num = Math.trunc(Number(value));
  if (!Number.isFinite(num) || num < 0) return null;
  return num;
}

const blockNonNegativeIntegerKeys = blockNonIntegerPercentKeys;

const STOCK_MODES = {
  VARIANT_ACTIVE: "variant_active",
  ALL_INSTOCK: "all_instock",
  THRESHOLD: "threshold",
  CORE_SIZE: "core_size",
};

const DEFAULT_CATEGORY_LEVELS = [
  { value: "categoryLevel1", label: "Category level 1" },
];

const PERCENT_OPTIONS = [5, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100];
const OPERATORS = [">", ">=", "<", "<=", "="];

function formatPct(value) {
  if (value == null || Number.isNaN(value)) return "0%";
  return `${Number(value).toFixed(1)}%`;
}

function formatCategoryLevelLabel(value) {
  if (!value) return "";
  return String(value)
    .replace(/_/g, " ")
    .replace(/([a-z])([0-9])/gi, "$1 $2")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function comparisonToOperator(comparison) {
  switch (comparison) {
    case "GT":
      return ">";
    case "GTE":
      return ">=";
    case "LT":
      return "<";
    case "LTE":
      return "<=";
    case "EQ":
      return "=";
    default:
      return ">=";
  }
}

function operatorToComparison(operator) {
  switch (operator) {
    case ">":
      return "GT";
    case ">=":
      return "GTE";
    case "<":
      return "LT";
    case "<=":
      return "LTE";
    case "=":
      return "EQ";
    default:
      return "GTE";
  }
}

const CORE_SIZE_API_BASE = process.env.REACT_APP_ADS_API;
const SIZE_RECOMMENDATIONS_API_BASE = process.env.REACT_APP_CATALOG_API;

function normalizeDefaultRule(rule = {}) {
  const pct = rule?.min_core_size_availability_pct;
  const abs = rule?.min_core_size_availability_abs;
  return {
    min_core_size_availability_pct:
      pct == null || pct === "" || Number.isNaN(Number(pct)) ? null : Number(pct),
    min_core_size_availability_abs:
      abs == null || abs === "" || Number.isNaN(Number(abs)) ? null : Number(abs),
    comparison_pct: rule.comparison_pct ?? rule.comparison ?? "GTE",
    comparison_abs: rule.comparison_abs ?? rule.comparison ?? "GTE",
  };
}

function categoryRowKey(catCfg) {
  if (!catCfg || typeof catCfg !== "object") return "";
  return String(catCfg.category_id ?? catCfg.category_name ?? "");
}

function rowsFromRecommendationApi(items) {
  return (items || []).map((row) => ({
    size: row.size,
    recommendation: Boolean(row.recommendation),
    total_product_sales_in_amount: Number(row.inventory_count) || 0,
    total_product_sales_in_percentage: Number(row.inventory_percentage) || 0,
  }));
}

function resolveStrategy(stockMode, thresholdMeasure) {
  if (stockMode === STOCK_MODES.VARIANT_ACTIVE) return "ANY_VARIANT";
  if (stockMode === STOCK_MODES.ALL_INSTOCK) return "ALWAYS_ACTIVE";
  if (stockMode === STOCK_MODES.THRESHOLD) {
    return thresholdMeasure === "number" ? "ABSOLUTE" : "PERCENTAGE";
  }
  return "CORE_SIZE";
}

function thresholdMeasureFromStrategy(strategy) {
  return strategy === "ABSOLUTE" ? "number" : "percentage";
}

function buildDefaultRulePayload({
  stockMode,
  thresholdMeasure,
  thresholdPercent,
  thresholdNumber,
  normDefaultRule,
  fallbackOperator,
  fallbackPercent,
  fallbackComparisonType,
  fallbackAbsQuantity,
}) {
  if (stockMode !== STOCK_MODES.THRESHOLD) {
    const parsedFallbackPercent = parsePercentInput(fallbackPercent);
    const parsedFallbackAbs = parseNonNegativeIntegerInput(fallbackAbsQuantity);
    return {
      min_core_size_availability_pct:
        fallbackComparisonType === "PCT"
          ? parsedFallbackPercent == null
            ? null
            : parsedFallbackPercent / 100
          : null,
      min_core_size_availability_abs:
        fallbackComparisonType === "ABS" ? parsedFallbackAbs : null,
      comparison_pct:
        fallbackComparisonType === "PCT" ? operatorToComparison(fallbackOperator) : null,
      comparison_abs:
        fallbackComparisonType === "ABS" ? operatorToComparison(fallbackOperator) : null,
      comparison_type: fallbackComparisonType,
    };
  }

  if (thresholdMeasure === "number") {
    return {
      min_core_size_availability_abs: Number(thresholdNumber) || 0,
      min_core_size_availability_pct: null,
      comparison_abs: normDefaultRule.comparison_abs,
      comparison_pct: null,
      comparison_type: "ABS",
    };
  }

  return {
    min_core_size_availability_pct: (Number(thresholdPercent) || 0) / 100,
    min_core_size_availability_abs: null,
    comparison_pct: normDefaultRule.comparison_pct,
    comparison_abs: null,
    comparison_type: "PCT",
  };
}

const CoreSize = () => {
  const dashboardSideBarVisibiltyState = useSelector(
    (state) => state.dashboardData.dashboardSideBarVisibiltyState
  );
  const { clientId, selectedPlatform } = useGlobalContext();

  const [remoteEnvelope, setRemoteEnvelope] = useState(null);
  const [sizeRecommendations, setSizeRecommendations] = useState({});
  const [hasPersistedConfig, setHasPersistedConfig] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [categoryConfigFetching, setCategoryConfigFetching] = useState(false);
  const [saving, setSaving] = useState(false);
  const [reloadTick, setReloadTick] = useState(0);

  const [logsModalOpen, setLogsModalOpen] = useState(false);

  const envelope = remoteEnvelope;

  const config = envelope?.config ?? {};
  const coreSizeConfig = config.core_size_config ?? {};
  const normDefaultRule = useMemo(
    () => normalizeDefaultRule(coreSizeConfig.default_rule),
    [coreSizeConfig.default_rule]
  );

  const defaultOperator = comparisonToOperator(normDefaultRule.comparison_pct);
  const defaultAbsOperator = comparisonToOperator(normDefaultRule.comparison_abs);

  const [stockMode, setStockMode] = useState(STOCK_MODES.VARIANT_ACTIVE);

  const [thresholdMeasure, setThresholdMeasure] = useState("percentage");
  const [thresholdPercent, setThresholdPercent] = useState(10);
  const [thresholdNumber, setThresholdNumber] = useState("");

  const [categoryLevelOptions, setCategoryLevelOptions] = useState(DEFAULT_CATEGORY_LEVELS);
  const [selectedCategoryLevel, setSelectedCategoryLevel] = useState(
    DEFAULT_CATEGORY_LEVELS[0].value
  );
  const [customAttributeMapping, setCustomAttributeMapping] = useState({});
  const categoryLevels = useMemo(
    () =>
      categoryLevelOptions.map((lvl) => ({
        ...lvl,
        label: getMappedCustomAttributeLabel(
          lvl.value,
          lvl.label,
          customAttributeMapping
        ),
      })),
    [categoryLevelOptions, customAttributeMapping]
  );
  const [categorySearch, setCategorySearch] = useState("");
  const hasHydratedStockModeRef = useRef(false);

  const [selectedSizes, setSelectedSizes] = useState({});
  const [availabilityRules, setAvailabilityRules] = useState({});

  const [fallbackOperator, setFallbackOperator] = useState(">=");
  const [fallbackPercent, setFallbackPercent] = useState("");
  const [fallbackComparisonType, setFallbackComparisonType] = useState("PCT");
  const [fallbackAbsQuantity, setFallbackAbsQuantity] = useState("");

  const [availAnchorEl, setAvailAnchorEl] = useState(null);
  const [availCategoryKey, setAvailCategoryKey] = useState(null);
  const [availDraftOp, setAvailDraftOp] = useState(">=");
  const [availDraftPercent, setAvailDraftPercent] = useState("");

  const [actionAnchorEl, setActionAnchorEl] = useState(null);
  const [actionCategoryKey, setActionCategoryKey] = useState(null);
  const [actionDraftOp, setActionDraftOp] = useState(">=");
  const [actionDraftQty, setActionDraftQty] = useState("");

  const [coreSizeIntroAutoOpen, setCoreSizeIntroAutoOpen] = useState(false);
  const coreSizeIntroShownOnceRef = useRef(false);

  useEffect(() => {
    let cancelled = false;

    const fetchCustomAttributeDefinition = async () => {
      if (!clientId || !selectedPlatform) {
        if (!cancelled) setCustomAttributeMapping({});
        return;
      }

      try {
        const res = await axios.get(
          `${CORE_SIZE_API_BASE}/client/${clientId}/custom-attribute-definition`,
          {
            params: {
              source: selectedPlatform,
              entity_type: "PRODUCT_INSIGHTS",
            },
          }
        );
        if (cancelled) return;
        setCustomAttributeMapping(res?.data?.custom_attribute_mapping ?? {});
      } catch (e) {
        if (!cancelled) setCustomAttributeMapping({});
      }
    };

    fetchCustomAttributeDefinition();
    return () => {
      cancelled = true;
    };
  }, [clientId, selectedPlatform]);

  useEffect(() => {
    let cancelled = false;

    const fetchCategoryLevels = async () => {
      if (!clientId || !selectedPlatform) {
        if (!cancelled) {
          setCategoryLevelOptions(DEFAULT_CATEGORY_LEVELS);
          setSelectedCategoryLevel(DEFAULT_CATEGORY_LEVELS[0].value);
        }
        return;
      }

      try {
        const res = await axios.get(
          `${SIZE_RECOMMENDATIONS_API_BASE}/client/${clientId}/core-size:category-levels`,
          { params: { source: selectedPlatform } }
        );
        if (cancelled) return;

        const levels = Array.isArray(res.data)
          ? res.data
              .filter(Boolean)
              .map((level) => ({
                value: String(level),
                label: formatCategoryLevelLabel(level),
              }))
          : [];
        const nextLevels = levels.length ? levels : DEFAULT_CATEGORY_LEVELS;
        setCategoryLevelOptions(nextLevels);
        setSelectedCategoryLevel((prev) =>
          nextLevels.some((lvl) => lvl.value === prev)
            ? prev
            : nextLevels[0].value
        );
      } catch (e) {
        if (cancelled) return;
        setCategoryLevelOptions(DEFAULT_CATEGORY_LEVELS);
        setSelectedCategoryLevel(DEFAULT_CATEGORY_LEVELS[0].value);
      }
    };

    fetchCategoryLevels();
    return () => {
      cancelled = true;
    };
  }, [clientId, selectedPlatform]);

  const rowDataByCategory = useMemo(() => {
    const acc = {};
    Object.entries(sizeRecommendations || {}).forEach(([key, rows]) => {
      acc[key] = rowsFromRecommendationApi(rows);
    });
    return acc;
  }, [sizeRecommendations]);

  const categories = useMemo(
    () => Object.keys(sizeRecommendations || {}).sort(),
    [sizeRecommendations]
  );

  const filteredCategories = useMemo(() => {
    const q = categorySearch.trim().toLowerCase();
    if (!q) return categories;
    return categories.filter((c) => c.toLowerCase().includes(q));
  }, [categories, categorySearch]);

  const hydrateFromApis = useCallback((env, recMap, { hydrateStockMode = true } = {}) => {
    const hasConfig = env?.config != null;
    const cfg = hasConfig ? env.config : {};
    const csc = cfg.core_size_config ?? {};
    const dr = normalizeDefaultRule(csc.default_rule);
    const fr = csc.fallback_rule ?? {};
    const rawDefaultRule = csc.default_rule ?? {};

    if (hydrateStockMode) {
      if (!hasConfig || !cfg.core_size_config) {
        setStockMode(STOCK_MODES.VARIANT_ACTIVE);
        setThresholdMeasure("percentage");
      } else {
        const strategy = csc.strategy;
        switch (strategy) {
          case "ANY_VARIANT":
            setStockMode(STOCK_MODES.VARIANT_ACTIVE);
            break;
          case "ALWAYS_ACTIVE":
            setStockMode(STOCK_MODES.ALL_INSTOCK);
            break;
          case "ABSOLUTE":
          case "PERCENTAGE":
            setStockMode(STOCK_MODES.THRESHOLD);
            setThresholdMeasure(thresholdMeasureFromStrategy(strategy));
            break;
          case "CORE_SIZE":
          default:
            setStockMode(STOCK_MODES.CORE_SIZE);
            break;
        }
      }
      hasHydratedStockModeRef.current = true;
    }

    // New accounts return config: null — never fill fallback with normalized 0 defaults.
    if (!hasConfig) {
      setFallbackComparisonType("PCT");
      setFallbackOperator(">=");
      setFallbackPercent("");
      setFallbackAbsQuantity("");
    } else {
      const rawPct =
        rawDefaultRule.min_core_size_availability_pct ??
        fr.min_total_variants_instock_pct;
      const rawAbs =
        rawDefaultRule.min_core_size_availability_abs ??
        fr.min_total_variants_instock_abs;
      const savedComparisonType =
        rawDefaultRule.comparison_type ?? fr.comparison_type;
      const nextFallbackComparisonType =
        savedComparisonType === "ABS" ||
        savedComparisonType === "PCT"
          ? savedComparisonType
          : isRuleValueSet(rawAbs) && Number(rawAbs) > 0 && !isRuleValueSet(rawPct)
            ? "ABS"
            : "PCT";
      setFallbackComparisonType(nextFallbackComparisonType);

      const rawComparison =
        nextFallbackComparisonType === "ABS"
          ? rawDefaultRule.comparison_abs ?? fr.comparison ?? dr.comparison_abs ?? "GTE"
          : rawDefaultRule.comparison_pct ?? fr.comparison ?? dr.comparison_pct ?? "GTE";
      setFallbackOperator(comparisonToOperator(rawComparison));

      setFallbackPercent(toFallbackPercentInputValue(rawPct));
      setFallbackAbsQuantity(toFallbackAbsInputValue(rawAbs));
    }

    // Only current category-level recommendations — drop removed / other-level saved categories.
    const keys = Object.keys(recMap || {}).sort();

    const nextSel = {};
    const nextRules = {};
    keys.forEach((key) => {
      const catCfg = (cfg.categories ?? []).find((c) => categoryRowKey(c) === key);
      const sizesFromRec = Array.isArray(recMap[key])
        ? recMap[key].map((row) => (typeof row === "string" ? row : row.size))
        : [];
      const saved = catCfg?.core_sizes ?? [];
      const filtered = saved.filter((sz) => !sizesFromRec.length || sizesFromRec.includes(sz));
      nextSel[key] = filtered.length ? filtered : saved;

      const custom = catCfg?.custom_rule;
      const pctRaw = custom?.min_core_size_availability_pct;
      const absRaw = custom?.min_core_size_availability_abs;
      const pctSet = isRuleValueSet(pctRaw);
      const absSet = isRuleValueSet(absRaw);

      nextRules[key] = {
        operator: comparisonToOperator(custom?.comparison_pct ?? "GTE"),
        percentage: pctSet ? Math.round(Number(pctRaw) * 100) : null,
        absOperator: comparisonToOperator(custom?.comparison_abs ?? "GTE"),
        absQuantity: absSet ? Number(absRaw) : null,
        comparison_pct: custom?.comparison_pct ?? "GTE",
        comparison_abs: custom?.comparison_abs ?? "GTE",
        min_core_size_availability_pct: pctSet ? Number(pctRaw) : null,
        min_core_size_availability_abs: absSet ? Number(absRaw) : null,
      };
    });
    setSelectedSizes(nextSel);
    setAvailabilityRules(nextRules);
  }, []);

  useEffect(() => {
    hasHydratedStockModeRef.current = false;
    coreSizeIntroShownOnceRef.current = false;
    setCoreSizeIntroAutoOpen(false);
    setFallbackPercent("");
    setFallbackAbsQuantity("");
    setFallbackComparisonType("PCT");
    setFallbackOperator(">=");
  }, [clientId, selectedPlatform]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!clientId) {
        if (!cancelled) {
          setRemoteEnvelope(null);
          setSizeRecommendations({});
          setHasPersistedConfig(false);
          setFetching(false);
          setCategoryConfigFetching(false);
        }
        return;
      }

      const shouldHydrateStockMode = !hasHydratedStockModeRef.current;
      // Full-page skeleton only on first load for this client/platform.
      if (shouldHydrateStockMode) {
        setFetching(true);
      } else {
        setCategoryConfigFetching(true);
      }
      try {
        const cfgRes = await axios.get(
          `${CORE_SIZE_API_BASE}/clients/${clientId}/core-size`,
          { params: { source: selectedPlatform, selected_category_level: selectedCategoryLevel } }
        );
        const recRes = await axios.get(
          `${SIZE_RECOMMENDATIONS_API_BASE}/client/${clientId}/categories:size-recommendations`,
          {
            params: {
              source: selectedPlatform,
              include_recommendation: true,
              selected_category_level: selectedCategoryLevel,
              page: 0,
              limit: 100,
            },
          }
        );
        if (cancelled) return;
        const env = cfgRes.data;
        const recMap = recRes.data && typeof recRes.data === "object" ? recRes.data : {};
        setRemoteEnvelope(env);
        setSizeRecommendations(recMap);
        setHasPersistedConfig(env?.config != null);
        hydrateFromApis(env, recMap, { hydrateStockMode: shouldHydrateStockMode });
      } catch (e) {
        const msg = e?.response?.data?.message || e?.message || "Failed to load core size config";
        toast.error(String(msg));
      } finally {
        if (!cancelled) {
          setFetching(false);
          setCategoryConfigFetching(false);
        }
      }
    };
    run();
    return () => {
      cancelled = true;
    };
    // hydrateFromApis is stable via useCallback([]).
  }, [clientId, selectedPlatform, selectedCategoryLevel, reloadTick, hydrateFromApis]);

  const toggleSize = useCallback((category, size, event) => {
    // Prevent clicks inside the radio label from re-triggering the radio
    // which can cause the browser to scroll to the top.
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    setSelectedSizes((prev) => {
      const set = new Set(prev[category] || []);
      if (set.has(size)) set.delete(size);
      else set.add(size);
      return { ...prev, [category]: Array.from(set) };
    });
  }, []);

  const hasAnyCoreSizeSelected = useMemo(
    () =>
      Object.values(selectedSizes || {}).some(
        (sizes) => Array.isArray(sizes) && sizes.length > 0
      ),
    [selectedSizes]
  );

  useEffect(() => {
    if (coreSizeIntroShownOnceRef.current) return;
    if (fetching || categoryConfigFetching) return;
    if (stockMode !== STOCK_MODES.CORE_SIZE) return;
    if (hasAnyCoreSizeSelected) return;

    coreSizeIntroShownOnceRef.current = true;
    setCoreSizeIntroAutoOpen(true);
  }, [fetching, categoryConfigFetching, stockMode, hasAnyCoreSizeSelected]);

  const openAvailabilityPopover = (event, categoryKey) => {
    setAvailAnchorEl(event.currentTarget);
    setAvailCategoryKey(categoryKey);
    const existing = availabilityRules[categoryKey];
    setAvailDraftOp(existing?.operator ?? defaultOperator);
    setAvailDraftPercent(
      isRuleValueSet(existing?.percentage) ? existing.percentage : ""
    );
  };

  const closeAvailabilityPopover = () => {
    setAvailAnchorEl(null);
    setAvailCategoryKey(null);
  };

  const applyAvailabilityUpdate = () => {
    if (!availCategoryKey) return;
    const percentValue = parsePercentInput(availDraftPercent);
    if (percentValue == null) {
      toast.error("Please enter Availability Stock % as an integer between 0 and 100.");
      return;
    }
    setAvailabilityRules((prev) => ({
      ...prev,
      [availCategoryKey]: {
        ...(prev[availCategoryKey] || {}),
        operator: availDraftOp,
        percentage: percentValue,
        comparison_pct: operatorToComparison(availDraftOp),
        min_core_size_availability_pct: percentValue / 100,
      },
    }));
    closeAvailabilityPopover();
  };

  const openActionPopover = (event, categoryKey) => {
    const existing = availabilityRules[categoryKey];
    setActionDraftOp(existing?.absOperator ?? defaultAbsOperator);
    setActionDraftQty(
      isRuleValueSet(existing?.absQuantity) ? existing.absQuantity : ""
    );
    setActionCategoryKey(categoryKey);
    setActionAnchorEl(event.currentTarget);
  };

  const closeActionPopover = () => {
    setActionAnchorEl(null);
    setActionCategoryKey(null);
  };

  const applyActionRuleUpdate = () => {
    if (!actionCategoryKey) return;
    const qtyValue = parseNonNegativeIntegerInput(actionDraftQty);
    if (qtyValue == null) {
      toast.error("Please enter Availability Stock as a non-negative integer.");
      return;
    }
    setAvailabilityRules((prev) => ({
      ...prev,
      [actionCategoryKey]: {
        ...(prev[actionCategoryKey] || {}),
        absOperator: actionDraftOp,
        absQuantity: qtyValue,
        comparison_abs: operatorToComparison(actionDraftOp),
        min_core_size_availability_abs: qtyValue,
      },
    }));
    closeActionPopover();
  };

  const renderInfoIcon = (title) => (
    <Tooltip title={title}>
      <InfoOutlinedIcon
        sx={{ fontSize: 16, color: "text.secondary", ml: 0.5, verticalAlign: "middle" }}
      />
    </Tooltip>
  );

  const handleSaveConfig = async () => {
    const strategy = resolveStrategy(stockMode, thresholdMeasure);
    const defaultRulePayload = buildDefaultRulePayload({
      stockMode,
      thresholdMeasure,
      thresholdPercent,
      thresholdNumber,
      normDefaultRule,
      fallbackOperator,
      fallbackPercent,
      fallbackComparisonType,
      fallbackAbsQuantity,
    });

    if (stockMode === STOCK_MODES.CORE_SIZE) {
      const currentCategoryKeys = Object.keys(sizeRecommendations || {});
      const missingAvailability = currentCategoryKeys.filter((key) => {
        const sizes = selectedSizes[key] || [];
        if (!Array.isArray(sizes) || sizes.length === 0) return false;
        const rule = availabilityRules[key] || {};
        const hasAbs = isRuleValueSet(rule.absQuantity);
        const hasPct = isRuleValueSet(rule.percentage);
        return !hasAbs && !hasPct;
      });
      if (missingAvailability.length > 0) {
        toast.error(
          "Set Availability Stock or Availability Stock % for each category with a core size selected."
        );
        return;
      }
    }

    const categoriesPayload =
      stockMode === STOCK_MODES.CORE_SIZE
        ? Object.keys(sizeRecommendations || {})
            .sort()
            .map((key) => {
              const rule = availabilityRules[key] || {};
              const coreSizes = selectedSizes[key] || [];
              const hasPct = isRuleValueSet(rule.percentage);
              const hasAbs = isRuleValueSet(rule.absQuantity);
              const pctDecimal = hasPct
                ? rule.min_core_size_availability_pct != null
                  ? Number(rule.min_core_size_availability_pct)
                  : Number(rule.percentage) / 100
                : null;
              const absVal = hasAbs
                ? rule.min_core_size_availability_abs != null
                  ? Number(rule.min_core_size_availability_abs)
                  : Number(rule.absQuantity)
                : null;
              return {
                category_name: key,
                core_sizes: coreSizes,
                use_default_rule: coreSizes.length === 0,
                custom_rule: {
                  min_core_size_availability_pct: pctDecimal,
                  min_core_size_availability_abs: absVal,
                  comparison_pct: hasPct
                    ? rule.comparison_pct ??
                      operatorToComparison(rule.operator ?? defaultOperator)
                    : null,
                  comparison_abs: hasAbs
                    ? rule.comparison_abs ??
                      operatorToComparison(rule.absOperator ?? defaultAbsOperator)
                    : null,
                },
              };
            })
        : [];

    const body = {
      config_type: config.config_type ?? "category_availability",
      selected_category_level: selectedCategoryLevel,
      use_color_as_parent: false,
      core_size_config: {
        enabled: true,
        strategy,
        default_rule: defaultRulePayload,
      },
      categories: categoriesPayload,
    };

    const payload = {
      success: true,
      client_id: envelope?.client_id ?? clientId ?? "",
      source: envelope?.source ?? selectedPlatform ?? "amazon",
      config: body,
    };

    // eslint-disable-next-line no-console
    console.log("core_size_config_payload", payload);

    if (!clientId) return;

    setSaving(true);
    try {
      const q = `?source=${encodeURIComponent(selectedPlatform)}`;
      const url = hasPersistedConfig
        ? `${CORE_SIZE_API_BASE}/clients/${clientId}/core-size${q}`
        : `${CORE_SIZE_API_BASE}/clients/${clientId}/core-size${q}`;
      if (hasPersistedConfig) {
        await axios.patch(url, body);
      } else {
        await axios.post(url, body);
      }
      await axios.post(`${SIZE_RECOMMENDATIONS_API_BASE}/clients/${clientId}:run-core-size?source=${encodeURIComponent(selectedPlatform)}`)

      toast.success("Configuration saved successfully.");
      setHasPersistedConfig(true);
      setReloadTick((t) => t + 1);
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || "Failed to save core size config";
      toast.error(String(msg));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className={
        dashboardSideBarVisibiltyState
          ? "main-content-container-onopen-mode"
          : "main-content-container-oncolapse-mode"
      }
      id="main-box"
    >
      <Box sx={{ width: "100%", mx: "auto", px: { xs: 1, sm: 2 } }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          spacing={2}
          sx={{ mb: 3 }}
        >
          <Box>
            <Typography variant="h6" fontWeight={700} color="text.primary">
              Define Stock Status
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              This indicates when we should mark a product as out of stock from your catalog.
            </Typography>
          </Box>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Button
              startIcon={<HistoryOutlined sx={{ fontSize: 18 }} />}
              sx={{ color: "text.secondary", textTransform: "none" }}
              onClick={() => setLogsModalOpen(true)}
            >
              VIEW LOGS
            </Button>
          </Stack>
        </Stack>

        <Stack direction={{ xs: "column", md: "row" }} spacing={3} alignItems="flex-start">
          <Typography
            fontWeight={500}
            sx={{ minWidth: { md: 220 }, maxWidth: 220 }}
          >
            How should the product stock status be interpreted?
          </Typography>

          <Paper
            variant="outlined"
            sx={{
              flex: 1,
              borderRadius: 2,
              overflow: "hidden",
              width: "100%",
            }}
          >
            {clientId && fetching ? (
              <Box
                sx={{
                  minHeight: 320,
                  p: 2,
                }}
              >
                <Stack spacing={1.5}>
                  <Skeleton variant="text" width="50%" height={32} />
                  <Skeleton variant="rectangular" width="100%" height={48} />
                  <Skeleton variant="rectangular" width="100%" height={48} />
                  <Skeleton variant="rectangular" width="100%" height={48} />
                  <Skeleton variant="rectangular" width="100%" height={180} />
                </Stack>
              </Box>
            ) : (
              <>
            <RadioGroup
              value={stockMode}
              onChange={(e) => setStockMode(e.target.value)}
            >
              <OptionRow
                value={STOCK_MODES.VARIANT_ACTIVE}
                selected={stockMode === STOCK_MODES.VARIANT_ACTIVE}
              >
                <Stack direction="row" alignItems="center" flexWrap="wrap">
                  Mark status is as Active even if one variant of a product is in-stock
                  {renderInfoIcon(
                    "If any variant has inventory, the parent product stays Active."
                  )}
                </Stack>
              </OptionRow>

              <OptionRow
                value={STOCK_MODES.ALL_INSTOCK}
                selected={stockMode === STOCK_MODES.ALL_INSTOCK}
              >
                Tag all products in the catalog as instock and mark the status of all the
                products as Active
              </OptionRow>

              <OptionRow
                value={STOCK_MODES.THRESHOLD}
                selected={stockMode === STOCK_MODES.THRESHOLD}
              >
                <Stack spacing={1.5} alignItems="flex-start">
                  <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1}>
                    Mark the status as Active, only when the total
                    <FormControl size="small" sx={{ minWidth: 130 }}>
                      <Select
                        value={thresholdMeasure}
                        onChange={(e) => setThresholdMeasure(e.target.value)}
                        disabled={stockMode !== STOCK_MODES.THRESHOLD}
                        size="small"
                        sx={{fontSize: "14px", background: "#fff", "& input": { fontSize: "14px" } }}
                      >
                        <MenuItem sx={{fontSize: "14px", background: "#fff", "& input": { fontSize: "14px" } }} value="percentage">Percentage</MenuItem>
                        <MenuItem sx={{fontSize: "14px", background: "#fff", "& input": { fontSize: "14px" } }} value="number">Number</MenuItem>
                      </Select>
                    </FormControl>
                    of in-stock variants is greater than equal to
                    {thresholdMeasure === "percentage" ? (
                      <FormControl size="small" sx={{ minWidth: 88 }}>
                        <Select
                          value={thresholdPercent}
                          onChange={(e) => setThresholdPercent(Number(e.target.value))}
                          disabled={stockMode !== STOCK_MODES.THRESHOLD}
                          size="small"
                          sx={{fontSize: "14px", background: "#fff", "& input": { fontSize: "14px" } }}
                        >
                          {PERCENT_OPTIONS.map((p) => (
                            <MenuItem sx={{fontSize: "14px", background: "#fff", "& input": { fontSize: "14px" } }} key={p} value={p}>
                              {p}%
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    ) : (
                      <TextField
                        size="small"
                        type="number"
                        placeholder="Number"
                        value={thresholdNumber}
                        onChange={(e) => setThresholdNumber(e.target.value)}
                        disabled={stockMode !== STOCK_MODES.THRESHOLD}
                        sx={{ width: 80, fontSize: "14px", background: "#fff", "& input": { fontSize: "14px" } }}
                        inputProps={{ min: 0 }}
                      />
                    )}
                  </Stack>
                </Stack>
              </OptionRow>

              <OptionRow
                value={STOCK_MODES.CORE_SIZE}
                selected={stockMode === STOCK_MODES.CORE_SIZE}
                noBorderBottom
              >
                <Stack spacing={0} width="100%">
                  <Stack direction="row" alignItems="center" flexWrap="wrap">
                    Mark the status on basis of core size configuration
                    {renderInfoIcon(
                      "Choose core sizes per category; availability rules apply per row."
                    )}
                  </Stack>

                 
                </Stack>
              </OptionRow>
            </RadioGroup>
             {stockMode === STOCK_MODES.CORE_SIZE && (
                    <Box
                      sx={{
                        // mt: 2,
                        // p: 2,
                        bgcolor: OPTION_ROW_SELECTED_BG,
                        // borderRadius: 2,
                        // border: `1px solid rgba(97, 72, 246, 0.15)`,
                      }}
                    >
                      <Paper variant="outlined" sx={{ mx: 2, borderRadius: 2, bgcolor: "#fff" }}>
                        <Stack spacing={2} p={2}>
                          <Stack direction="row" alignItems="center" spacing={0.5}>
                            <Typography>
                              Configure Core size based on
                            </Typography>
                            <Tooltip title="Group configuration by category depth.">
                              <InfoOutlinedIcon sx={{ fontSize: 18, color: "text.secondary" }} />
                            </Tooltip>
                          </Stack>
                          <FormControl sx={{ width: "280px" }} size="small">
                            <Select
                              value={selectedCategoryLevel}
                              onChange={(e) => {
                                setSelectedCategoryLevel(e.target.value);
                                setSelectedSizes({});
                                setAvailabilityRules({});
                                setCategorySearch("");
                              }}
                              sx={{fontSize: "14px", background: "#fff", "& input": { fontSize: "14px" } }}
                            >
                              {categoryLevels.map((lvl) => (
                                <MenuItem sx={{fontSize: "14px", background: "#fff", "& input": { fontSize: "14px" } }} key={lvl.value} value={lvl.value}>
                                  {lvl.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>

                          
                          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop:2 }}>
                          <TextField
                            size="small"
                            placeholder="Search..."
                            value={categorySearch}
                            onChange={(e) => setCategorySearch(e.target.value)}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <SearchIcon fontSize="small" color="action" />
                                </InputAdornment>
                              ),
                            }}
                            variant="standard"
                            sx={{ width: "280px" }}
                          />
                          {/* <Stack
                            direction="row"
                            justifyContent="flex-end"
                            alignItems="center"
                            spacing={0.5}
                            width={"fit-content"}
                          >
                            <StarIcon sx={{ fontSize: 18, color: PRIMARY }} />
                            <Typography variant="body2" fontWeight={600}>
                              Recommended
                            </Typography>
                            <Tooltip title="Sizes highlighted from sales data.">
                              <InfoOutlinedIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                            </Tooltip>
                          </Stack> */}
                          </Box>
                          

                          <TableContainer sx={{ maxHeight: 420, borderRadius: 2, border: `1px solid rgba(97, 72, 246, 0.15)` }}>
                            <Table size="small" stickyHeader>
                              <TableHead>
                                <TableRow>
                                  <TableCell
                                    sx={{
                                      fontWeight: 700,
                                      bgcolor: "grey.50",
                                      py: 1.75,
                                    }}
                                  >
                                    Categories
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      fontWeight: 700,
                                      bgcolor: "grey.50",
                                      py: 1.75,
                                    }}
                                  >
                                    <ExportDownloadTooltip
                                      autoOpen={coreSizeIntroAutoOpen}
                                      autoOpenDurationMs={8000}
                                      hintPlacement="top"
                                      showCloseButton
                                      onHintClose={() => setCoreSizeIntroAutoOpen(false)}
                                      title={CORE_SIZE_COLUMN_HINT}
                                      snackbarMessage={CORE_SIZE_COLUMN_HINT}
                                    >
                                      <Stack direction="row" alignItems="center" spacing={0.5}>
                                        <span>Core Size</span>
                                        <InfoOutlinedIcon
                                          sx={{ fontSize: 16, color: "text.secondary" }}
                                        />
                                      </Stack>
                                    </ExportDownloadTooltip>
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      fontWeight: 700,
                                      bgcolor: "grey.50",
                                      width: 170,
                                      py: 1.75,
                                    }}
                                  >
                                    <Stack direction="row" alignItems="center" spacing={0.5}>
                                      <span>Availability Stock</span>
                                      <Tooltip title={AVAILABILITY_STOCK_COLUMN_DESC}>
                                        <InfoOutlinedIcon
                                          sx={{ fontSize: 16, color: "text.secondary" }}
                                        />
                                      </Tooltip>
                                    </Stack>
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      fontWeight: 700,
                                      bgcolor: "grey.50",
                                      width: 190,
                                      py: 1.75,
                                    }}
                                  >
                                    <Stack direction="row" alignItems="center" spacing={0.5}>
                                      <span>Availability Stock %</span>
                                      <Tooltip title={AVAILABILITY_STOCK_COLUMN_DESC}>
                                        <InfoOutlinedIcon
                                          sx={{ fontSize: 16, color: "text.secondary" }}
                                        />
                                      </Tooltip>
                                    </Stack>
                                  </TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {categoryConfigFetching ? (
                                  Array.from({ length: 5 }).map((_, index) => (
                                    <TableRow key={`category-config-skeleton-${index}`}>
                                      <TableCell sx={{ py: 1.5 }}>
                                        <Skeleton variant="text" width="80%" height={28} />
                                      </TableCell>
                                      <TableCell sx={{ py: 1.5 }}>
                                        <Stack direction="row" flexWrap="wrap" gap={1}>
                                          {Array.from({ length: 4 }).map((__, chipIndex) => (
                                            <Skeleton
                                              key={`category-config-chip-skeleton-${chipIndex}`}
                                              variant="rounded"
                                              width={72}
                                              height={48}
                                            />
                                          ))}
                                        </Stack>
                                      </TableCell>
                                      <TableCell align="right" sx={{ py: 1.5 }}>
                                        <Skeleton variant="rounded" width={70} height={32} sx={{ ml: "auto" }} />
                                      </TableCell>
                                      <TableCell sx={{ py: 1.5 }}>
                                        <Skeleton variant="rounded" width={70} height={32} />
                                      </TableCell>
                                    </TableRow>
                                  ))
                                ) : (
                                  filteredCategories.map((cat) => {
                                  const rows = rowDataByCategory[cat] || [];
                                  const selected = new Set(selectedSizes[cat] || []);
                                  const availabilityRule = availabilityRules[cat] || {
                                    operator: defaultOperator,
                                    percentage: null,
                                    absOperator: defaultAbsOperator,
                                    absQuantity: null,
                                  };
                                  const absDisplay = isRuleValueSet(availabilityRule.absQuantity)
                                    ? `${availabilityRule.absOperator} ${availabilityRule.absQuantity}`
                                    : `${availabilityRule.absOperator ?? defaultAbsOperator}`;
                                  const pctDisplay = isRuleValueSet(availabilityRule.percentage)
                                    ? `${availabilityRule.operator} ${availabilityRule.percentage}%`
                                    : `${availabilityRule.operator ?? defaultOperator} %`;
                                  return (
                                    <TableRow key={cat} hover>
                                      <TableCell sx={{ verticalAlign: "top", py: 1.5 }}>
                                        {cat}
                                      </TableCell>
                                      <TableCell sx={{ minWidth: 280, maxWidth: 480, py: 1.5 }}>
                                        <Stack direction="row" flexWrap="wrap" gap={1}>
                                          {rows.map((row) => {
                                            const isOn = selected.has(row.size);
                                            const rec = row.recommendation;
                                            return (
                                              <Box
                                                key={row.size}
                                                onClick={(e) =>
                                                  toggleSize(cat, row.size, e)
                                                }
                                                sx={{
                                                  position: "relative",
                                                  width: 72,
                                                  py: 0.75,
                                                  px: 0.5,
                                                  borderRadius: 1,
                                                  border: "1px solid",
                                                  borderColor: isOn
                                                    ? PRIMARY
                                                    : "divider",
                                                  bgcolor: isOn
                                                    ? TILE_RECOMMENDED_BG
                                                    : "background.paper",
                                                  cursor: "pointer",
                                                  textAlign: "center",
                                                }}
                                              >
                                                {rec && (
                                                  <StarIcon
                                                    sx={{
                                                      position: "absolute",
                                                      top: 2,
                                                      left: 2,
                                                      fontSize: 12,
                                                      color: PRIMARY,
                                                    }}
                                                  />
                                                )}
                                                <Typography
                                                  variant="caption"
                                                  display="block"
                                                //   fontWeight={600}
                                                  sx={{ mt: rec ? 0.5 : 0 }}
                                                >
                                                  {row.size}
                                                </Typography>
                                                <Tooltip
                                                  title={`Inventory Count: ${Number(
                                                    row.total_product_sales_in_amount ?? 0
                                                  ).toLocaleString()}`}
                                                >
                                                  <Stack
                                                    direction="row"
                                                    alignItems="center"
                                                    justifyContent="center"
                                                    spacing={0.25}
                                                  >
                                                    <FlashOnIcon
                                                      sx={{ fontSize: 12, color: "warning.main" }}
                                                    />
                                                    <Typography
                                                      variant="caption"
                                                      color="text.secondary"
                                                    >
                                                      {formatPct(row.total_product_sales_in_percentage)}
                                                    </Typography>
                                                  </Stack>
                                                </Tooltip>
                                              </Box>
                                            );
                                          })}
                                        </Stack>
                                      </TableCell>
                                      <TableCell align="right" sx={{ verticalAlign: "middle", py: 1.5 }}>
                                        <Button
                                          variant="outlined"
                                          size="small"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            openActionPopover(e, cat);
                                          }}
                                          sx={{
                                            textTransform: "none",
                                            borderColor: "divider",
                                            color: "text.primary",
                                            minWidth: 70,
                                          }}
                                        >
                                          {absDisplay}
                                        </Button>
                                      </TableCell>
                                      <TableCell sx={{ verticalAlign: "middle", py: 1.5 }}>
                                        <Button
                                          variant="outlined"
                                          size="small"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            openAvailabilityPopover(e, cat);
                                          }}
                                          sx={{
                                            textTransform: "none",
                                            borderColor: "divider",
                                            color: "text.primary",
                                            minWidth: 70,
                                          }}
                                        >
                                          {pctDisplay}
                                        </Button>
                                      </TableCell>
                                    </TableRow>
                                  );
                                  })
                                )}
                              </TableBody>
                            </Table>
                          </TableContainer>

                          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: "grey.50" }}>
                            <Typography variant="body2" sx={{ mb: 1.5, fontWeight: 500 }}>
                              For the categories where no core size has been selected mark status as
                              active if total{" "}
                              {fallbackComparisonType === "PCT" ? "% of " : ""}in-stock variants is
                            </Typography>
                            <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1}>
                              <FormControl size="small" sx={{ minWidth: 120 }}>
                                <Select
                                  value={fallbackComparisonType}
                                  onChange={(e) => setFallbackComparisonType(e.target.value)}
                                >
                                  <MenuItem value="PCT">Percentage</MenuItem>
                                  <MenuItem value="ABS">Absolute</MenuItem>
                                </Select>
                              </FormControl>
                              <FormControl size="small" sx={{ minWidth: 72 }}>
                                <Select
                                  value={fallbackOperator}
                                  onChange={(e) => setFallbackOperator(e.target.value)}
                                >
                                  {OPERATORS.map((op) => (
                                    <MenuItem key={op} value={op}>
                                      {op}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                              {fallbackComparisonType === "PCT" ? (
                                <Stack direction="row" alignItems="center" spacing={0.75}>
                                  <TextField
                                    key={`fallback-pct-${clientId || "none"}`}
                                    size="small"
                                    type="text"
                                    inputMode="numeric"
                                    autoComplete="off"
                                    value={
                                      fallbackPercent == null || fallbackPercent === ""
                                        ? ""
                                        : String(fallbackPercent)
                                    }
                                    onChange={(e) =>
                                      setFallbackPercent(sanitizePercentInput(e.target.value))
                                    }
                                    onKeyDown={blockNonIntegerPercentKeys}
                                    placeholder="Number"
                                    sx={{ width: 120, background: "#fff" }}
                                    inputProps={{ inputMode: "numeric" }}
                                  />
                                  <Typography variant="body2">%</Typography>
                                </Stack>
                              ) : (
                                <TextField
                                  size="small"
                                  type="text"
                                  inputMode="numeric"
                                  autoComplete="off"
                                  value={
                                    fallbackAbsQuantity == null || fallbackAbsQuantity === ""
                                      ? ""
                                      : String(fallbackAbsQuantity)
                                  }
                                  onChange={(e) =>
                                    setFallbackAbsQuantity(
                                      sanitizeNonNegativeIntegerInput(e.target.value)
                                    )
                                  }
                                  onKeyDown={blockNonNegativeIntegerKeys}
                                  placeholder="Number"
                                  sx={{ width: 120, background: "#fff" }}
                                  inputProps={{ inputMode: "numeric", min: 0 }}
                                />
                              )}
                            </Stack>
                          </Paper>

                          
                        </Stack>
                        
                      </Paper>
                      
                    </Box>
                  )}
                  <Stack sx={{background: OPTION_ROW_SELECTED_BG }} p={2} direction="row" justifyContent="flex-end" spacing={1.5}>
                            <Button variant="outlined" sx={{ color: PRIMARY, borderColor: PRIMARY }}>
                              CANCEL
                            </Button>
                            <Button
                              variant="contained"
                              color="primary"
                              disabled={saving || (Boolean(clientId) && !remoteEnvelope)}
                              onClick={handleSaveConfig}
                            >
                              {saving ? "SAVING…" : "SAVE"}
                            </Button>
                          </Stack>
              </>
            )}
          </Paper>
        </Stack>
      </Box>

      <Popover
        open={Boolean(availAnchorEl)}
        anchorEl={availAnchorEl}
        onClose={closeAvailabilityPopover}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Box sx={{ p: 1.2, width: 280, maxWidth: "100%" }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Consider a product as available if
          </Typography>
          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 1.25 }}>
            <FormControl size="small" sx={{ minWidth: 72 }}>
              <Select value={availDraftOp} onChange={(e) => setAvailDraftOp(e.target.value)}>
                {OPERATORS.map((op) => (
                  <MenuItem key={op} value={op}>
                    {op}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              size="small"
              type="text"
              inputMode="numeric"
              value={availDraftPercent}
              onChange={(e) => setAvailDraftPercent(sanitizePercentInput(e.target.value))}
              onKeyDown={blockNonIntegerPercentKeys}
              placeholder="Number"
              inputProps={{ min: 0, max: 100, inputMode: "numeric" }}
              sx={{ width: 120 }}
            />
            <Typography variant="body2">%</Typography>
          </Stack>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            of its selected core size variants are in-stock.
          </Typography>
          <Stack direction="row" justifyContent="flex-end" spacing={1}>
            <Button
              variant="outlined"
              size="small"
              onClick={closeAvailabilityPopover}
              sx={{ px: 1.5 }}
            >
              CANCEL
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={applyAvailabilityUpdate}
              sx={{ px: 1.5 }}
            >
              SAVE
            </Button>
          </Stack>
        </Box>
      </Popover>

      <Popover
        open={Boolean(actionAnchorEl)}
        anchorEl={actionAnchorEl}
        onClose={closeActionPopover}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Box sx={{ p: 1.2, width: 360, maxWidth: "100%" }}>
          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 1.25 }} flexWrap="wrap">
            <Typography variant="body2">
              Consider variant as in-stock if inventory quantity is
            </Typography>
            <FormControl size="small" sx={{ minWidth: 72 }}>
              <Select value={actionDraftOp} onChange={(e) => setActionDraftOp(e.target.value)}>
                {OPERATORS.map((op) => (
                  <MenuItem key={op} value={op}>
                    {op}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              size="small"
              type="text"
              inputMode="numeric"
              autoComplete="off"
              value={actionDraftQty ?? ""}
              onChange={(e) =>
                setActionDraftQty(sanitizeNonNegativeIntegerInput(e.target.value))
              }
              onKeyDown={blockNonNegativeIntegerKeys}
              placeholder="Number"
              inputProps={{ inputMode: "numeric", min: 0 }}
              sx={{ width: 120 }}
            />
            <Typography variant="body2">.</Typography>
          </Stack>
          <Stack direction="row" justifyContent="flex-end" spacing={1}>
            <Button variant="outlined" size="small" onClick={closeActionPopover} sx={{ px: 1.5 }}>
              CANCEL
            </Button>
            <Button variant="contained" size="small" onClick={applyActionRuleUpdate} sx={{ px: 1.5 }}>
              SAVE
            </Button>
          </Stack>
        </Box>
      </Popover>
      <CoreSizeLogsModal
        open={logsModalOpen}
        onClose={() => setLogsModalOpen(false)}
        clientId={clientId}
        source={selectedPlatform}
      />
    </div>
  );
};

function OptionRow({ value, selected, children, noBorderBottom }) {
  return (
    <Box
      sx={{
        px: 2,
        py: 2,
        borderBottom: noBorderBottom ? "none" : "1px solid",
        borderColor: "divider",
        bgcolor: selected ? OPTION_ROW_SELECTED_BG : "background.paper",
      }}
    >
      <FormControlLabel
        value={value}
        control={
          <Radio
            sx={{
              color: PRIMARY,
              "&.Mui-checked": { color: PRIMARY },
              py: 0.5,
              alignSelf: "center",
            }}
          />
        }
        label={<Box sx={{ width: "100%" }}>{children}</Box>}
        sx={{
          alignItems: "center",
          m: 0,
          width: "100%",
          // Decrease label size a bit and stabilize the vertical rhythm
          "& .MuiFormControlLabel-label": {
            fontSize: "0.875rem",
            lineHeight: 1.25,
            my: 0,
          },
        }}
      />
    </Box>
  );
}

export default CoreSize;
